// The chart renders the API response verbatim, so it uses the response schema's
// own point type rather than restating those three fields locally.
import type { LeaseDailyRewardPoint } from "@swarm/shared/schemas";
import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getLeaseDailyRewards } from "@/lib/api/license-leases";
import currency from "@/lib/currency";

interface LeasePerformanceChartProps {
  ownerAddress: string;
  onDataChange?: (data: LeaseDailyRewardPoint[]) => void;
  hasActiveLeases?: boolean;
}

export const LeasePerformanceChart: React.FC<LeasePerformanceChartProps> = ({
  ownerAddress,
  onDataChange,
  hasActiveLeases = false,
}) => {
  const selectedPeriod = 7; // Fixed to 7 days
  const [data, setData] = useState<LeaseDailyRewardPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDailyRewards = async (days: number) => {
    if (!ownerAddress) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getLeaseDailyRewards(ownerAddress, days);

      // The schema already guarantees numbers; `Number()` stays because recharts
      // renders `NaN` as a silently missing point and a future string column
      // would be invisible here otherwise.
      const chartData = result.data.map((item) => ({
        ...item,
        daily_reward: Number(item.daily_reward),
        accumulative_reward: Number(item.accumulative_reward),
      }));

      setData(chartData);

      // Pass data back to parent component
      if (onDataChange) {
        onDataChange(chartData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyRewards(selectedPeriod);
  }, [ownerAddress]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (value: number) => {
    return currency.convert.toFormattedDigits(value);
  };

  const formatAbbreviatedCurrency = (value: number) => {
    if (value >= 1000000) {
      const millions = value / 1000000;

      return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
    } else if (value >= 1000) {
      const thousands = value / 1000;

      return thousands % 1 === 0 ? `${thousands}k` : `${thousands.toFixed(1)}k`;
    }

    return value.toString();
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center" id="lease-performance-chart-loading">
        <p className="text-gray-500" id="lease-performance-chart-loading-text">
          Loading chart data...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center" id="lease-performance-chart-error">
        <p className="text-red-500" id="lease-performance-chart-error-text">
          Error: {error}
        </p>
      </div>
    );
  }

  if (data.length === 0) {
    const message = hasActiveLeases
      ? "System collecting reward information. Please check later..."
      : "No data available. Lease your licenses to earn.";

    return (
      <div className="flex h-64 items-center justify-center" id="lease-performance-chart-empty">
        <p className="text-gray-500" id="lease-performance-chart-empty-text">
          {message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" id="lease-performance-chart">
      {/* Chart */}
      <div className="h-64" id="lease-performance-chart-canvas">
        <ResponsiveContainer height="100%" width="100%">
          <LineChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid stroke="#374151" strokeDasharray="3 3" />
            <XAxis dataKey="date" fontSize={12} stroke="#9CA3AF" tickFormatter={formatDate} />
            <YAxis
              domain={["dataMin", "dataMax"]}
              fontSize={12}
              stroke="#9CA3AF"
              tickFormatter={formatAbbreviatedCurrency}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#1F2937",
                border: "1px solid #374151",
                borderRadius: "8px",
                color: "#F3F4F6",
              }}
              formatter={(value: number | undefined) => [
                value !== undefined ? formatCurrency(value) : "0",
              ]}
              labelFormatter={(label) => `Date: ${formatDate(label)}`}
            />
            <Line
              dataKey="accumulative_reward"
              dot={{ fill: "#10B981", strokeWidth: 2, r: 5 }}
              stroke="#10B981"
              strokeWidth={3}
              type="monotone"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
